import { detectBrowserRegion } from '@common/core-layer/utils/regional-host'

import type { GrecaptchaInstance, LoadRecaptchaScriptOptions } from '../types/captcha.types'

const RECAPTCHA_SCRIPT_ID = 'recaptcha-script'
const RECAPTCHA_SCRIPT_URL = 'https://www.google.com/recaptcha/api.js?render=explicit'
const DEFAULT_TIMEOUT_MS = 8000
const GRECAPTCHA_POLL_INTERVAL_MS = 25

// Public site keys (not secrets) — env vars override. AU uses a region-specific Google key.
const DEFAULT_RECAPTCHA_SITE_KEY = '6LeA0l8pAAAAAMjF87jhpIngHsYPUOsWzM9RwNac'
const AU_RECAPTCHA_SITE_KEY = '6LelTvstAAAAAGnuhRYrQrrOOoh6mFiHBkw7Lvo0'

const getExistingScript = (): HTMLScriptElement | null => {
  if (typeof document === 'undefined') return null
  return document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null
}

const waitForGrecaptcha = async (timeoutMs: number): Promise<GrecaptchaInstance | null> => {
  const startedAt = Date.now()
  while (Date.now() - startedAt < timeoutMs) {
    if (typeof window !== 'undefined' && window.grecaptcha) {
      return window.grecaptcha
    }
    await new Promise<void>(resolve => {
      setTimeout(resolve, GRECAPTCHA_POLL_INTERVAL_MS)
    })
  }
  return null
}

export const getRecaptchaSiteKey = (): string => {
  const env = import.meta.env as Record<string, string | boolean | undefined>
  const fromVite = typeof env.VITE_RECAPTCHA_SITE_KEY === 'string' ? env.VITE_RECAPTCHA_SITE_KEY.trim() : ''
  const fromNuxtPublic = typeof env.NUXT_PUBLIC_RECAPTCHA_SITE_KEY === 'string' ? env.NUXT_PUBLIC_RECAPTCHA_SITE_KEY.trim() : ''
  if (fromVite || fromNuxtPublic) {
    return fromVite || fromNuxtPublic
  }

  // Pre-login safe: pod SERVE_REGION / AU portal host (no token required).
  return detectBrowserRegion() === 'au' ? AU_RECAPTCHA_SITE_KEY : DEFAULT_RECAPTCHA_SITE_KEY
}

export const loadRecaptchaScript = async (options?: LoadRecaptchaScriptOptions): Promise<GrecaptchaInstance | null> => {
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null
  }

  if (window.grecaptcha) {
    return window.grecaptcha
  }

  const existing = getExistingScript()
  if (existing) {
    const fromPoll = await waitForGrecaptcha(timeoutMs)
    if (fromPoll) {
      return fromPoll
    }
    // Stale tag (e.g. prior onerror left the node, or load never completed): drop it so a retry can inject again.
    existing.remove()
  }

  const script = document.createElement('script')
  script.id = RECAPTCHA_SCRIPT_ID
  script.src = RECAPTCHA_SCRIPT_URL
  script.async = true
  script.defer = true
  if (options?.nonce) {
    script.nonce = options.nonce
  }

  const loaded = new Promise<void>((resolve, reject) => {
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'))
  })

  document.head.appendChild(script)

  try {
    await loaded
  } catch {
    script.remove()
    return null
  }

  return waitForGrecaptcha(timeoutMs)
}
